// External Insight Provider — public API (Issue #22).

import type { DependencyGraph } from "../dependency/types.js";
import { integrateInsights } from "./integrator.js";
import { runProviders } from "./registry.js";
import type {
  InsightProvider,
  InsightQuery,
  IntegrationResult,
} from "./types.js";

export * from "./types.js";
export { CommandProvider, CommandProviderError } from "./command-provider.js";
export type { CommandProviderConfig } from "./command-provider.js";
export {
  integrateInsight,
  integrateInsights,
  resolveEdgeKind,
} from "./integrator.js";
export type { IntegrateInsightResult } from "./integrator.js";
export { runProviders } from "./registry.js";
export type { RunProvidersResult } from "./registry.js";

function copyDependencyGraph(graph: DependencyGraph): DependencyGraph {
  return {
    ast: graph.ast,
    nodes: new Map(graph.nodes),
    edges: [...graph.edges],
    artifactMapping: new Map(graph.artifactMapping),
  };
}

/**
 * Enrich a dependency graph with edges from external insight providers.
 * When `providers` is empty, returns the input graph unchanged (shallow copy).
 */
export async function integrateExternalInsights(
  depGraph: DependencyGraph,
  providers: InsightProvider[],
  query: InsightQuery,
): Promise<IntegrationResult> {
  if (providers.length === 0) {
    return {
      graph: copyDependencyGraph(depGraph),
      unresolvedEdges: [],
      providerErrors: [],
      stats: {
        totalEdgesAdded: 0,
        providersSucceeded: 0,
        providersFailed: 0,
      },
    };
  }

  const { insights, errors } = await runProviders(providers, query);
  const { edges: newEdges, unresolvedEdges } = integrateInsights(insights);

  const graph = copyDependencyGraph(depGraph);
  const priorCount = graph.edges.length;
  graph.edges.push(...newEdges);

  return {
    graph,
    unresolvedEdges,
    providerErrors: errors,
    stats: {
      totalEdgesAdded: graph.edges.length - priorCount,
      providersSucceeded: insights.length,
      providersFailed: errors.length,
    },
  };
}
